import type {
  Deck,
  DeckSummary,
  Note,
  NoteSearchResult,
  CreateNoteRequest,
  UpdateDeckRequest,
  UpdateNoteRequest,
  ReviewRequest,
  StudyQueue,
  ImageUploadResponse,
  KoreanLookupResult,
  SwedishLookupResult,
  GuardSettingsResponse,
  UpdateGuardSettingsRequest,
} from "@glosor/shared";

class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    credentials: "same-origin",
    ...init,
    headers: {
      ...(init.body && !(init.body instanceof FormData) ? { "content-type": "application/json" } : {}),
      ...(init.headers ?? {}),
    },
  });
  if (!res.ok) {
    let msg = `${res.status}`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) msg = body.error;
    } catch {}
    throw new ApiError(res.status, msg);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export { ApiError };

export const api = {
  async me(): Promise<{ authenticated: boolean }> {
    return request("/api/auth/me");
  },
  async login(password: string): Promise<void> {
    await request("/api/auth/login", { method: "POST", body: JSON.stringify({ password }) });
  },
  async logout(): Promise<void> {
    await request("/api/auth/logout", { method: "POST" });
  },
  async listDecks(): Promise<DeckSummary[]> {
    return request("/api/decks");
  },
  async createDeck(name: string): Promise<Deck> {
    return request("/api/decks", { method: "POST", body: JSON.stringify({ name }) });
  },
  async renameDeck(id: number, name: string): Promise<Deck> {
    return request(`/api/decks/${id}`, { method: "PATCH", body: JSON.stringify({ name }) });
  },
  async updateDeck(id: number, patch: UpdateDeckRequest): Promise<Deck> {
    return request(`/api/decks/${id}`, { method: "PATCH", body: JSON.stringify(patch) });
  },
  async increaseNewToday(id: number, amount: number): Promise<Deck> {
    return request(`/api/decks/${id}/increase-new-today`, {
      method: "POST",
      body: JSON.stringify({ amount }),
    });
  },
  async deleteDeck(id: number): Promise<void> {
    return request(`/api/decks/${id}`, { method: "DELETE" });
  },
  async listNotes(deckId: number): Promise<Note[]> {
    return request(`/api/notes?deckId=${deckId}`);
  },
  async searchNotes(q: string, deckId?: number): Promise<NoteSearchResult[]> {
    const params = new URLSearchParams({ q });
    if (deckId !== undefined) params.set("deckId", String(deckId));
    return request(`/api/notes/search?${params.toString()}`);
  },
  async createNote(body: CreateNoteRequest): Promise<Note> {
    return request("/api/notes", { method: "POST", body: JSON.stringify(body) });
  },
  async updateNote(id: number, body: UpdateNoteRequest): Promise<Note> {
    return request(`/api/notes/${id}`, { method: "PATCH", body: JSON.stringify(body) });
  },
  async deleteNote(id: number): Promise<void> {
    return request(`/api/notes/${id}`, { method: "DELETE" });
  },
  async studyNext(deckId: number): Promise<StudyQueue> {
    return request(`/api/study/next?deckId=${deckId}`);
  },
  async review(body: ReviewRequest): Promise<StudyQueue> {
    return request("/api/study/review", { method: "POST", body: JSON.stringify(body) });
  },
  async undoReview(deckId: number): Promise<StudyQueue> {
    return request("/api/study/undo", { method: "POST", body: JSON.stringify({ deckId }) });
  },
  async uploadImage(file: File): Promise<ImageUploadResponse> {
    const fd = new FormData();
    fd.append("file", file);
    return request("/api/images", { method: "POST", body: fd });
  },
  imageUrl(filename: string): string {
    return `/api/images/${filename}`;
  },
  async lookupKorean(
    q: string,
    opts: { refresh?: boolean; signal?: AbortSignal } = {},
  ): Promise<KoreanLookupResult> {
    const params = new URLSearchParams({ q });
    if (opts.refresh) params.set("refresh", "true");
    return request(`/api/dictionary/lookup?${params.toString()}`, { signal: opts.signal });
  },
  async lookupSwedish(
    q: string,
    opts: { signal?: AbortSignal } = {},
  ): Promise<SwedishLookupResult> {
    const params = new URLSearchParams({ q, lang: "sv" });
    return request(`/api/dictionary/lookup?${params.toString()}`, { signal: opts.signal });
  },
  async getGuardSettings(): Promise<GuardSettingsResponse> {
    return request("/api/guard/settings");
  },
  async updateGuardSettings(body: UpdateGuardSettingsRequest): Promise<GuardSettingsResponse> {
    return request("/api/guard/settings", { method: "PUT", body: JSON.stringify(body) });
  },
  async getLogs(
    lines: number,
    opts: { signal?: AbortSignal } = {},
  ): Promise<{ ok: boolean; text?: string; error?: string; stderr?: string }> {
    return request(`/api/logs?lines=${lines}`, { signal: opts.signal });
  },
};
